import { formatIdr, type Idr } from "@kopi/domain";
import { INTL_LOCALE, type Locale } from "./locale";

/** Numbers, money and dates in the interface language (R1-09e); the currency is always rupiah. */
export interface Formatter {
  /** "Rp45.000" / "Rp45,000". */
  money(amount: Idr): string;
  /** A decimal quantity string ("1234.5"), up to its six decimals. */
  quantity(value: string): string;
  /** Date and time, in `timeZone` (an outlet's) or the device's. */
  dateTime(iso: string, timeZone?: string): string;
  /** The time of day alone, 24-hour: "10.05" / "10:05", in `timeZone` or the device's. */
  time(iso: string, timeZone?: string): string;
  /** A date alone: "12 Okt 2026". */
  date(date: Date): string;
  /** A ratio as a whole percentage, without a sign: 0.125 → "13%". */
  percent(ratio: number): string;
}

function createFormatter(locale: Locale): Formatter {
  const tag = INTL_LOCALE[locale];
  const quantity = new Intl.NumberFormat(tag, { maximumFractionDigits: 6 });
  const date = new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", year: "numeric" });
  const percent = new Intl.NumberFormat(tag, { style: "percent", maximumFractionDigits: 0 });
  // Intl formats are costly to build: one per style and timezone, reused.
  const cached = new Map<string, Intl.DateTimeFormat>();
  const inZone = (style: "dateTime" | "time", timeZone: string | undefined) => {
    const key = `${style}:${timeZone ?? ""}`;
    let format = cached.get(key);
    if (!format) {
      format = new Intl.DateTimeFormat(tag, {
        ...(style === "dateTime" ? { dateStyle: "medium" } : {}),
        timeStyle: "short",
        hourCycle: "h23",
        ...(timeZone ? { timeZone } : {}),
      });
      cached.set(key, format);
    }
    return format;
  };
  return {
    money: (amount) => formatIdr(amount, tag),
    quantity: (value) => quantity.format(Number(value)),
    dateTime: (iso, timeZone) => inZone("dateTime", timeZone).format(new Date(iso)),
    time: (iso, timeZone) => inZone("time", timeZone).format(new Date(iso)),
    date: (value) => date.format(value),
    percent: (ratio) => percent.format(Math.abs(ratio)),
  };
}

const formatters = new Map<Locale, Formatter>();

/** The (cached) formatter for `locale`. */
export function formatterFor(locale: Locale): Formatter {
  let formatter = formatters.get(locale);
  if (!formatter) {
    formatter = createFormatter(locale);
    formatters.set(locale, formatter);
  }
  return formatter;
}
